import React, { useState, useEffect } from "react";
import { createUserWithEmailAndPassword, deleteUser, signOut } from "firebase/auth";
import { auth, db } from "../firebase/config";
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import bgImage from "../assets/imagen3.jpeg";
import {
  isRequired,
  isEmail,
  isStrongPassword,
  isPhoneNumber,
} from "../utils/validators";

const cleanInput = (val, max = 50) =>
  String(val || "")
    .trim()
    .replace(/[<>'"`;(){}[\]\\]/g, "")
    .slice(0, max);

const cleanPhone = (val) => String(val || "").replace(/[^\d+]/g, "").slice(0, 15);

const RegisterScreen = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    celular: "",
    email: "",
    password: "",
    confirmarPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem("selectedPlan");
    if (!raw) {
      navigate("/plans", { replace: true });
      return;
    }
    try {
      setSelectedPlan(JSON.parse(raw));
    } catch {
      localStorage.removeItem("selectedPlan");
      navigate("/plans", { replace: true });
    }
  }, [navigate]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const nextErrors = {
      nombre: isRequired(form.nombre),
      apellido: isRequired(form.apellido),
      celular: isPhoneNumber(form.celular) || isRequired(form.celular),
      email: isEmail(form.email) || isRequired(form.email),
      password: isStrongPassword(form.password) || isRequired(form.password),
      confirmarPassword:
        form.password !== form.confirmarPassword ? "Las contraseñas no coinciden." : null,
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).every((err) => !err);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (!validate()) return;
    if (!selectedPlan) {
      setApiError("Debes seleccionar un plan para continuar.");
      return;
    }

    const email = cleanInput(form.email, 80).toLowerCase();
    const isFreeTrial = Boolean(selectedPlan.isLaunchTrial);

    setSubmitting(true);
    let createdAuthUser = null;

    try {
      if (isFreeTrial) {
        const leadRef = collection(db, "prospectos_leads");
        const leadQuery = query(leadRef, where("email", "==", email));
        const leadSnap = await getDocs(leadQuery);

        if (leadSnap.empty) {
          setApiError(
            "Este correo electrónico no figura en la lista de prelanzamiento. Selecciona uno de nuestros planes regulares para continuar."
          );
          setSubmitting(false);
          return;
        }
      }

      const cred = await createUserWithEmailAndPassword(auth, email, form.password);
      createdAuthUser = cred.user;

      const now = new Date();
      const trialEndDate = new Date(now);
      trialEndDate.setDate(now.getDate() + 7);

      const profile = {
        uid: createdAuthUser.uid,
        nombre: cleanInput(form.nombre, 40),
        apellido: cleanInput(form.apellido, 40),
        celular: cleanPhone(form.celular),
        email,
        rol: "usuario",
        isAdmin: false,
        accesos: 0,
        plan: isFreeTrial ? "trial" : selectedPlan.id,
        planNombreReportado: isFreeTrial ? "Semana Gratis (Lanzamiento)" : selectedPlan.name,
        subscriptionStatus: isFreeTrial ? "active" : "pending_payment",
        estado: isFreeTrial ? "active" : "pending_payment",
        haUsadoTrial: true,
        trialUtilizado: true,
        eliminado: false,
        isDeleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(isFreeTrial && {
          planStart: Timestamp.fromDate(now),
          planEnd: Timestamp.fromDate(trialEndDate),
          promoCanje: true,
        }),
      };

      await setDoc(doc(db, "usuarios", createdAuthUser.uid), profile);
      localStorage.removeItem("selectedPlan");

      navigate(isFreeTrial ? "/home" : "/pago-pendiente", { replace: true });
    } catch (err) {
      if (createdAuthUser) {
        try {
          await deleteUser(createdAuthUser);
          await signOut(auth);
        } catch {
          // No-op
        }
      }

      if (err.code === "auth/email-already-in-use") {
        setApiError("Este correo electrónico ya se encuentra registrado. Inicia sesión.");
      } else if (err.code === "auth/weak-password") {
        setApiError("La contraseña debe tener al menos 6 caracteres.");
      } else if (err.code === "auth/invalid-email") {
        setApiError("El formato del correo electrónico no es válido.");
      } else {
        setApiError("No se pudo completar el registro. Intenta nuevamente.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div style={styles.overlay}>
        <div style={styles.card}>
          <button onClick={() => navigate("/plans")} style={styles.backBtn} type="button">
            <FaArrowLeft size={12} /> Volver a planes
          </button>

          <h2 style={styles.title}>Registro</h2>
          {selectedPlan && (
            <p style={styles.planInfo}>
              Plan seleccionado: <strong>{selectedPlan.name}</strong>
            </p>
          )}

          {apiError && <div style={styles.alertError}>{apiError}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div>
              <input
                type="text"
                placeholder="Nombre"
                value={form.nombre}
                onChange={handleChange("nombre")}
                style={styles.input}
                maxLength={40}
                disabled={submitting}
              />
              {errors.nombre && <span style={styles.fieldError}>{errors.nombre}</span>}
            </div>

            <div>
              <input
                type="text"
                placeholder="Apellido"
                value={form.apellido}
                onChange={handleChange("apellido")}
                style={styles.input}
                maxLength={40}
                disabled={submitting}
              />
              {errors.apellido && <span style={styles.fieldError}>{errors.apellido}</span>}
            </div>

            <div>
              <input
                type="tel"
                placeholder="Celular"
                value={form.celular}
                onChange={handleChange("celular")}
                style={styles.input}
                maxLength={15}
                disabled={submitting}
              />
              {errors.celular && <span style={styles.fieldError}>{errors.celular}</span>}
            </div>

            <div>
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange("email")}
                style={styles.input}
                maxLength={80}
                disabled={submitting}
              />
              {errors.email && <span style={styles.fieldError}>{errors.email}</span>}
            </div>

            <div>
              <input
                type="password"
                placeholder="Contraseña"
                value={form.password}
                onChange={handleChange("password")}
                style={styles.input}
                disabled={submitting}
              />
              {errors.password && <span style={styles.fieldError}>{errors.password}</span>}
            </div>

            <div>
              <input
                type="password"
                placeholder="Confirmar contraseña"
                value={form.confirmarPassword}
                onChange={handleChange("confirmarPassword")}
                style={styles.input}
                disabled={submitting}
              />
              {errors.confirmarPassword && (
                <span style={styles.fieldError}>{errors.confirmarPassword}</span>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.submitBtn,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Cargando..." : "Crear cuenta"}
            </button>
          </form>

          <div style={styles.footerLink}>
            <span>¿Ya tienes cuenta? </span>
            <button type="button" onClick={() => navigate("/login")} style={styles.linkBtn}>
              Inicia sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    backdropFilter: "blur(3px)",
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(16px)",
    width: "100%",
    maxWidth: 420,
    borderRadius: 20,
    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.15)",
    padding: "28px 24px",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    border: "1px solid rgba(255, 255, 255, 0.7)",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#b03060",
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    marginBottom: 10,
    padding: 0,
  },
  title: {
    color: "#1e293b",
    textAlign: "center",
    fontSize: 22,
    fontWeight: 700,
    margin: "0 0 4px 0",
  },
  planInfo: {
    fontSize: 12.5,
    color: "#64748b",
    textAlign: "center",
    margin: "0 0 16px 0",
  },
  alertError: {
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    padding: "9px 12px",
    borderRadius: 8,
    marginBottom: 14,
    fontSize: 12,
    textAlign: "center",
    lineHeight: 1.4,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 11,
    width: "100%",
  },
  input: {
    padding: "10px 13px",
    width: "100%",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13.5,
    boxSizing: "border-box",
    outline: "none",
    color: "#1e293b",
    backgroundColor: "#ffffff",
    fontFamily: "inherit",
  },
  fieldError: {
    display: "block",
    color: "#dc2626",
    fontSize: 11,
    marginTop: 2,
    fontWeight: 500,
    textAlign: "left",
  },
  submitBtn: {
    marginTop: 6,
    padding: "11px",
    backgroundColor: "#b03060",
    color: "#ffffff",
    border: "none",
    borderRadius: 50,
    width: "100%",
    fontSize: 13.5,
    fontWeight: 600,
    boxShadow: "0 4px 12px rgba(176, 48, 96, 0.25)",
  },
  footerLink: {
    marginTop: 14,
    textAlign: "center",
    fontSize: 12.5,
    color: "#475569",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#b03060",
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
    padding: 0,
  },
};

export default RegisterScreen;